import { useState, useEffect } from "react";
import {
  X,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  TrendingUp,
  Target,
  Sparkles,
  Loader2,
} from "lucide-react";
import { api } from "../lib/api";

const ACTION_STYLE = {
  BUY: "bg-moss text-bone-50 border-moss",
  ACCUMULATE: "bg-moss-bg text-moss-light border-moss-light/50",
  HOLD: "bg-amber_brand-bg text-amber_brand border-amber_brand/50",
  TRIM: "bg-terracotta-bg text-terracotta border-terracotta/50",
  SELL: "bg-terracotta text-bone-50 border-terracotta",
};

export default function DigestModal({ ticker, companyName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`/stocks/${encodeURIComponent(ticker)}/digest`, { timeout: 180000 })
      .then((res) => {
        if (!cancelled) {
          setData(res.data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.detail || err?.message || "Failed");
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ticker]);

  const d = data?.digest;
  const v = d?.final_verdict;
  const sources = data?.sources_used || {};

  return (
    <div
      className="fixed inset-0 z-[100] bg-stone_brand-dark/70 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      onClick={onClose}
      data-testid="digest-modal"
    >
      <div
        className="bg-bone-50 border border-line rounded-sm w-full max-w-4xl my-4 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-line px-6 py-4 flex items-center justify-between rounded-t-sm z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-moss flex items-center justify-center rounded-sm flex-shrink-0">
              <Sparkles className="w-4 h-4 text-bone-50" />
            </div>
            <div>
              <div className="overline text-stone_brand-light mb-0.5">— AI Research Digest</div>
              <div className="font-serif-display text-xl text-stone_brand-dark font-medium leading-tight">
                {companyName || ticker}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-stone_brand hover:text-stone_brand-dark hover:bg-bone-100 rounded-sm transition-colors"
            data-testid="digest-close-btn"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 lg:p-8 space-y-6">
          {loading ? (
            <div className="py-16 text-center">
              <Loader2 className="w-8 h-8 text-moss animate-spin mx-auto mb-4" />
              <div className="font-serif-display text-xl text-stone_brand-dark mb-2">
                Synthesizing digest…
              </div>
              <p className="text-sm text-stone_brand">
                Cross-checking Fairvest AI, Screener.in pros/cons, and analyst consensus.
                Takes 15-25 seconds.
              </p>
            </div>
          ) : error ? (
            <div className="py-12 text-center">
              <AlertCircle className="w-8 h-8 text-terracotta mx-auto mb-3" />
              <div className="font-serif-display text-xl text-stone_brand-dark mb-1">
                Couldn't build digest
              </div>
              <p className="text-sm text-stone_brand">{error}</p>
            </div>
          ) : !d ? (
            <div className="py-12 text-center text-stone_brand">No digest data.</div>
          ) : (
            <>
              {/* Sources strip */}
              <div className="flex flex-wrap items-center gap-2" data-testid="digest-sources">
                <span className="overline text-stone_brand-light">Sources:</span>
                <SourceBadge ok={sources.fairvest} label="Fairvest AI" />
                <SourceBadge ok={sources.screener} label="Screener.in" />
                <SourceBadge ok={sources.analyst_consensus} label="Analyst Consensus" />
              </div>

              {/* Final Verdict — hero card */}
              {v && (
                <div
                  className={`border rounded-sm p-5 lg:p-6 ${ACTION_STYLE[v.action] || ACTION_STYLE.HOLD}`}
                  data-testid="digest-verdict"
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <div className="overline opacity-80 mb-1.5">— Final Verdict</div>
                      <div className="font-serif-display text-4xl font-semibold tracking-tight">
                        {v.action}
                      </div>
                    </div>
                    <span className="font-mono text-xs border border-current/40 px-2.5 py-1 rounded-sm uppercase tracking-widest opacity-90">
                      {v.conviction} CONVICTION
                    </span>
                  </div>
                  <p className="text-sm lg:text-base leading-relaxed mb-4 opacity-95">
                    {v.one_liner}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                    <VerdictMetric label="Entry Zone" value={v.entry_zone_str} />
                    <VerdictMetric label="12-Mo Target" value={v.target_12mo_str} />
                    <VerdictMetric label="Stop-Loss" value={v.stop_loss_str || "—"} />
                  </div>
                </div>
              )}

              {/* Executive Summary */}
              {d.executive_summary && (
                <section>
                  <div className="overline mb-2">— Executive Summary</div>
                  <p className="font-serif-display text-lg lg:text-xl italic text-stone_brand-dark leading-relaxed">
                    "{d.executive_summary}"
                  </p>
                </section>
              )}

              {/* Valuation Snapshot */}
              {d.valuation_snapshot && (
                <section className="bg-white border border-line rounded-sm p-5 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="digest-valuation">
                  <ValBox label="Current Price" value={d.valuation_snapshot.current_price_str} />
                  <ValBox label="Fairvest Fair Value" value={d.valuation_snapshot.fairvest_fair_value_str} accent="text-moss" />
                  <ValBox label="Analyst Target" value={d.valuation_snapshot.analyst_target_str} />
                  <ValBox label="Margin of Safety" value={d.valuation_snapshot.margin_of_safety_pct_str} accent="text-moss-light" />
                </section>
              )}

              {/* Agreements */}
              {d.agreements?.length > 0 && (
                <Section
                  icon={<CheckCircle2 className="w-4 h-4 text-moss" />}
                  title="Where Sources Agree"
                  items={d.agreements}
                  variant="good"
                  testid="digest-agreements"
                />
              )}

              {/* Contradictions */}
              {d.contradictions?.length > 0 && (
                <Section
                  icon={<AlertTriangle className="w-4 h-4 text-amber_brand" />}
                  title="Where Sources Disagree"
                  items={d.contradictions}
                  variant="warn"
                  testid="digest-contradictions"
                />
              )}

              {/* Green / Red flags in 2 columns */}
              <div className="grid md:grid-cols-2 gap-4">
                {d.green_flags?.length > 0 && (
                  <Section
                    icon={<TrendingUp className="w-4 h-4 text-moss-light" />}
                    title="Green Flags"
                    items={d.green_flags}
                    variant="good"
                    compact
                    testid="digest-green-flags"
                  />
                )}
                {d.red_flags?.length > 0 && (
                  <Section
                    icon={<AlertCircle className="w-4 h-4 text-terracotta" />}
                    title="Red Flags"
                    items={d.red_flags}
                    variant="bad"
                    compact
                    testid="digest-red-flags"
                  />
                )}
              </div>

              {/* Catalysts */}
              {d.catalysts_12mo?.length > 0 && (
                <Section
                  icon={<Target className="w-4 h-4 text-moss" />}
                  title="12-Month Catalysts"
                  items={d.catalysts_12mo}
                  variant="good"
                  testid="digest-catalysts"
                />
              )}

              <p className="text-[0.7rem] font-mono text-stone_brand-light tracking-wider uppercase pt-4 border-t border-line">
                — Not investment advice. Digest consolidates Fairvest AI (Claude Sonnet 4.5),
                Screener.in algorithmic pros/cons, and Yahoo Finance analyst consensus.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SourceBadge({ ok, label }) {
  return (
    <span
      className={`text-[0.65rem] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${
        ok
          ? "bg-moss-bg text-moss-light border-moss-light/40"
          : "bg-bone-100 text-stone_brand-light border-line"
      }`}
    >
      {ok ? "✓" : "—"} {label}
    </span>
  );
}

function VerdictMetric({ label, value }) {
  return (
    <div className="bg-bone-50/15 border border-current/20 rounded-sm p-3">
      <div className="text-[0.65rem] font-mono uppercase tracking-widest opacity-80 mb-1">
        {label}
      </div>
      <div className="font-mono text-sm font-semibold">{value || "—"}</div>
    </div>
  );
}

function ValBox({ label, value, accent = "text-stone_brand-dark" }) {
  return (
    <div>
      <div className="overline mb-1.5">{label}</div>
      <div className={`font-mono text-lg font-semibold ${accent}`}>{value || "—"}</div>
    </div>
  );
}

function Section({ icon, title, items, variant = "good", compact = false, testid }) {
  const bg = {
    good: "bg-moss-bg/60 border-moss-light/30",
    warn: "bg-amber_brand-bg/70 border-amber_brand/40",
    bad: "bg-terracotta-bg/70 border-terracotta/40",
  }[variant];
  return (
    <section className={`${bg} border rounded-sm p-5`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <div className="overline">— {title}</div>
      </div>
      <ul className={compact ? "space-y-1.5" : "space-y-2"}>
        {items.map((it, i) => (
          <li key={i} className="flex gap-2 text-sm text-stone_brand-dark leading-relaxed">
            <span className="text-stone_brand-light font-mono text-xs flex-shrink-0 mt-1">
              {String(i + 1).padStart(2, "0")}
            </span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
